import {CompassWidgetAppearanceType, CompassWidgetConfig} from "areas/metrics/widgets/Compass/config";
import Highcharts, {SeriesGaugeOptions} from "highcharts";
import {buildBackground} from "components/HighchartsWrapper";
import {MetricInfo} from "framework/entities/metricInfo";
import {getValueFormatter, ValueFormatter} from "areas/metrics/components/MetricSelector/utils/getValueFormatter";
import {generateLegendString} from "areas/metrics/components/MetricLegendSelect/utils/generateLegendString";
import {LabelPosition, LabelVisibility} from "areas/metrics/components/LabelOptionsEditor/types";

function getLabelDistances(config: CompassWidgetConfig) {
	const labels = config.appearance.labels
	if (labels.direction.position == LabelPosition.Outside) {
		if (labels.tick.position == LabelPosition.Outside || labels.tick.visibility == LabelVisibility.Hidden) {
			return ['140%','120%']
		} else {
			return ['120%', '80%']
		}
	} else {
		if (labels.tick.position == LabelPosition.Outside || labels.tick.visibility == LabelVisibility.Hidden) {
			return ['80%', '120%']
		} else {
			return ["60%", "80%"]
		}
	}
}

export function getHighchartsConfig(config: CompassWidgetConfig, info: MetricInfo, value: number) {
	const name = generateLegendString({
		template: config.appearance.legendTemplate,
		data: info.qualifier
	})

	const valueFormatter = getValueFormatter(config.metric, info);
	let chartConfig: Highcharts.Options

	switch (config.appearance.type) {
		case CompassWidgetAppearanceType.Compass:
			chartConfig = addInvertedDial(getCompassConfig(config, name, value, valueFormatter), config)
			break

		case CompassWidgetAppearanceType.Radial:
			chartConfig = getRadialConfig(config, name, value, valueFormatter)
			break

		default:
		case CompassWidgetAppearanceType.Generic:
			chartConfig = addInvertedDial(getGenericConfig(config, name, value, valueFormatter), config)
			break
	}

	chartConfig.tooltip = {
		useHTML: true,
		formatter: function (e) {
			const tooltip =  generateLegendString({
				template: config.appearance.tooltipTemplate,
				data: info.qualifier,
				y: this.point.y
			});

			if(!tooltip)
				return false;

			return tooltip;
		}
	}


	return chartConfig;
}

function getCompassConfig(config: CompassWidgetConfig, name: string, value: number, valueFormatter: ValueFormatter) {
	const labelPositions = getLabelDistances(config);

	const appearance = config.appearance;

	const directionIndicatorsAxis = {
		min: 0,
		max: 360,
		tickInterval: 45,
		labels: {
			formatter: function () {
				const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
				return dirs[(this.value / 45) % 8];
			},
			distance: labelPositions[0],
			style: {
				fontSize: appearance.labels.direction.size + 'px',
				fontWeight: 'bold',
				color: appearance.labels.direction.color
			}
		},
		lineWidth: 0,
		tickLength: 0,
		minorTickLength: 0,
		lineColor: appearance.axisColor,
		tickColor: appearance.axisColor,
		minorTickColor: appearance.axisColor,
		minorGridLineColor: appearance.axisColor,
	} as Highcharts.YAxisOptions

	const angleAxis = {
		min: 0,
		max: 360,
		tickInterval: 30,
		labels: {
			formatter: function () {
				return this.value + '°';
			},
			enabled: appearance.labels.tick.visibility != LabelVisibility.Hidden,
			distance: labelPositions[1],
			style: {
				color: appearance.labels.tick.color,
				fontSize: appearance.labels.tick.size + 'px'
			}
		},
		lineColor: appearance.axisColor,
		tickColor: appearance.axisColor,
		minorTickColor: appearance.axisColor,
		minorGridLineColor: appearance.axisColor,
		lineWidth: 0,
		tickLength: 5,
		minorTickLength: 3,
		showFirstLabel: false
	} as Highcharts.YAxisOptions

	const yAxis: Highcharts.YAxisOptions[] = []

	if (appearance.labels.direction.visibility != LabelVisibility.Hidden) {
		yAxis.push(directionIndicatorsAxis)
	}

	if (appearance.labels.tick.visibility != LabelVisibility.Hidden) {
		yAxis.push(angleAxis)
	}

	if (appearance.labels.direction.visibility == LabelVisibility.Hidden
		&& config.appearance.labels.tick.visibility == LabelVisibility.Hidden) {
		yAxis.push(angleAxis)
	}

	const bothOutside = appearance.labels.direction.position == LabelPosition.Outside && appearance.labels.tick.position == LabelPosition.Outside
	const oneOutside = appearance.labels.direction.position == LabelPosition.Outside || appearance.labels.tick.position == LabelPosition.Outside

	return {
		chart: {
			type: 'gauge'
		},
		subtitle: getSubtitle(name, config),
		pane: {
			size: bothOutside
				? '70%'
				: oneOutside
					? '80%'
					: '90%',
			startAngle: 0,
			endAngle: 360,
			background: buildCompassBackground(config)
		},
		yAxis: yAxis,
		series: [{
			data: [value],
			dataLabels: {
				enabled: config.appearance.labels.value.visibility != LabelVisibility.Hidden,
				format: valueFormatter(value),
				borderWidth: 0,
				style: {
					color: config.appearance.labels.value.color,
					fontSize: config.appearance.labels.value.size + 'px',
					textOutline: 'none',
				}
			},
			name,
			type: 'gauge',
			dial: {
				backgroundColor: config.appearance.indicatorColor,
				baseWidth: 8
			},
			pivot: {
				backgroundColor: config.appearance.indicatorColor
			},
		}],
		tooltip: {
			formatter: function(){
				return valueFormatter(this.y)
			}
		}
	} as Highcharts.Options
}

function getRadialConfig(config: CompassWidgetConfig, name: string, value: number,  valueFormatter: ValueFormatter) {
	return {
		chart: {
			type: 'solidgauge'
		},
		subtitle: getSubtitle(name, config),
		pane: {
			startAngle: 0,
			endAngle: 360,
			background: [{
				backgroundColor: config.appearance.paneBackgroundColor.startColor,
				borderWidth: 0
			},{ // Track for the value background
				outerRadius: '100%',
				innerRadius: '80%',
				backgroundColor: `color-mix(in srgb, ${config.appearance.indicatorColor} 20%, transparent)`,
				borderWidth: 0
			}]
		},

		yAxis: {
			min: config.minValue,
			max: config.maxValue,
			lineWidth: 0,
			tickInterval: (config.maxValue - config.minValue) / config.appearance.ticksCount,
			labels: {
				enabled: config.appearance.labels.tick.visibility !== LabelVisibility.Hidden,
				distance: config.appearance.labels.tick.position == LabelPosition.Inside ? '-70%' : 15,
				style: {
					color: config.appearance.labels.tick.color,
					fontSize: config.appearance.labels.tick.size + 'px'
				}
			}
		},

		plotOptions: {
			solidgauge: {
				linecap: 'round',
				stickyTracking: false,
			}
		},
		tooltip: {
			formatter: function(){
				return valueFormatter(this.y)
			}
		},
		series: [{
			name,
			type: 'solidgauge',
			dataLabels: {
				enabled: config.appearance.labels.value.visibility != LabelVisibility.Hidden,
				format: valueFormatter(value),
				borderWidth: 0,
				style: {
					color: config.appearance.labels.value.color,
					textOutline: 'none',
					fontSize: config.appearance.labels.value.size + 'px'
				}
			},
			data: [{
				color: config.appearance.indicatorColor,
				radius: '100%',
				innerRadius: '80%',
				y: value
			}],
		}]
	} as Highcharts.Options
}

function getGenericConfig(config: CompassWidgetConfig, name: string, value: number, valueFormatter: ValueFormatter) {
	const appearance = config.appearance
	return {
		chart: {
			type: 'gauge',
			alignTicks: false
		},
		subtitle: getSubtitle(name, config),
		pane: {
			startAngle: 0,
			endAngle: 360,
			background: buildCompassBackground(config),
			size: appearance.labels.tick.position == LabelPosition.Outside ? '80%' : '100%'
		},
		yAxis: [
			{
				min: config.minValue,
				max: config.maxValue,
				showLastLabel: false,
				tickInterval: Math.floor((config.maxValue - config.minValue) / config.appearance.ticksCount),
				labels: {
					distance: config.appearance.labels.tick.position == LabelPosition.Inside ? -20 : 20,
					style: {
						color: config.appearance.labels.tick.color,
						fontSize: config.appearance.labels.tick.size + 'px',
					}
				},
				lineWidth: 0,
				tickLength: 5,
				minorTickLength: 3,
				lineColor: config.appearance.axisColor,
				tickColor: config.appearance.axisColor,
				minorTickColor: config.appearance.axisColor,
				minorGridLineColor: config.appearance.axisColor,
			}
		],
		tooltip: {
			formatter: function(){
				return valueFormatter(this.y)
			}
		},
		series: [{
			data: [value],
			type: 'gauge',
			dataLabels: {
				enabled: config.appearance.labels.value.visibility != LabelVisibility.Hidden,
				format: valueFormatter(value),
				borderWidth: 0,
				style: {
					color: config.appearance.labels.legend.color,
					fontSize: config.appearance.labels.legend.size + 'px',
					textOutline: 'none'
				}
			},
			name,
			pivot: {
				backgroundColor: config.appearance.indicatorColor
			},
			dial: {
				backgroundColor: config.appearance.indicatorColor,
				baseWidth: 8
			},
		}]
	} as Highcharts.Options
}


type GetSubtitleExtra = {
	outsideDistance?: number
}

function addInvertedDial(chartConfig: Highcharts.Options, config: CompassWidgetConfig){
	if(!config.appearance.invertDial)
		return chartConfig

	chartConfig.chart.events = {
		render: function () {
			renderInvertedDial(this, config)
		}
	}

	const series = chartConfig.series[0] as SeriesGaugeOptions
	series.dial = {
		backgroundColor: 'transparent',
		borderWidth: 0,
		rearLength: '0%',
		radius: '0%'
	}

	return chartConfig
}


function renderInvertedDial(chart: Highcharts.Chart & {customDial?: Highcharts.SVGElement}, config: CompassWidgetConfig){
	if(!config.appearance.invertDial)
		return

	const series = chart.series[0]
	const point = series.points[0]
	if (!point)
		return;

	const yAxis = chart.yAxis[0] as Highcharts.Axis & {
		translate: (y: number) => number,
		center: number[]
	};

	//the value in radians
	const angle = yAxis.translate(point.y);
	const angleInDegrees = (angle + Math.PI / 2) * 180 / Math.PI

	//getting center coordinates
	const center = yAxis.center; // [x, y, radius]
	const cx = chart.plotLeft + center[0]
	const cy = chart.plotTop + center[1]

	if (!chart.customDial) {
		chart.customDial = chart.renderer
			.path([
				'M', -6.624, -4,
				'L', 46.367999999999995, -4,
				'L', 66.24, -0.5,
				'L', 66.24, 0.5,
				'L', 46.367999999999995, 4,
				'L', -6.624, 4,
				'Z'] as const)

			.attr({
				stroke: config.appearance.indicatorColor,
				fill: config.appearance.indicatorColor,
				'stroke-width': 1,
				'stroke-linecap': 'round',
				transform: `translate(${cx}, ${cy}) rotate(${angleInDegrees} 0 0 )`,
				zIndex: 6
			})
			.add();
	} else {
		chart.customDial.animate({
			transform: `translate(${cx}, ${cy}) rotate(${angleInDegrees} 0 0 )`,
		},
		{
			duration: 800,
			easing: 'easeOutBounce'
		})
	}
}

function getSubtitle(name: string, config: CompassWidgetConfig, extra?: GetSubtitleExtra) {
	if (config.appearance.labels.legend.visibility == LabelVisibility.Hidden)
		return null

	return {
		text: name,
		align: 'center',
		verticalAlign: config.appearance.labels.legend.position,
		y:config.appearance.labels.legend.position == LabelPosition.Middle ? -10 : 0,
		style: {
			color: config.appearance.labels.legend.color,
			fontSize: config.appearance.labels.legend.size + 'px'
		}
	} as Highcharts.SubtitleOptions;
}

function buildCompassBackground(config: CompassWidgetConfig){
	return [{
		...buildBackground(config.appearance.paneBackgroundColor),
		borderColor: config.appearance.axisColor
	}]
}
